import { AfterContentInit, AfterViewInit, Component, ElementRef } from "@angular/core";
import { HeroImage, HeroImageService, HeroImageStore } from "./state";
import { environment } from "src/environments/environment";
import { Router, NavigationEnd } from "@angular/router";
import { NgIf } from "@angular/common";
import { AuthQuery } from "../login-module/auth/state";

/**
 * The `esr-hero-image-banner` component serves as a visually appealing header
 * for the website. It rotates through different photos.
 *
 * The component smoothly transitions from the active photo into the website's
 * main background color using CSS shapes.
 */
@Component({
    selector: "esr-hero-image-banner",
    styleUrls: ["./hero-image-banner.component.scss"],
    templateUrl: "./hero-image-banner.component.html",
    imports: [NgIf]
})
export class HeroImageBannerComponent implements AfterContentInit, AfterViewInit {
    public lustrumImage: HeroImage | undefined;
    public image: HeroImage | undefined;
    public isLoggedIn$ = false;
    public isNewsPage = false;

    constructor(
        private elRef: ElementRef,
        private service: HeroImageService,
        private router: Router,
        private authQuery: AuthQuery
    ) {
        this.handleRouteChange();

        router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.handleRouteChange();
            }
        });

        this.authQuery.isLoggedIn$.subscribe(
            (isLoggedIn) => {
                this.isLoggedIn$ = isLoggedIn;
                this.playBackgroundVideo();
            }
        );

        setTimeout(() => this.playBackgroundVideo(), 0);
        setTimeout(() => this.playBackgroundVideo(), 250);
    }

    private handleRouteChange() {
        const url = this.router.url || window.location.pathname;
        this.isNewsPage = url === "/news" || url === "/news/";
        if (this.router.url.includes("lustrum")) {
            if (this.lustrumImage) {
                this.setImage(this.lustrumImage);
            }
        } else {
            if (this.image) {
                this.setImage(this.image);
            }
        }
        this.playBackgroundVideo();
    }

    ngAfterContentInit() {
        // get random image
        this.service.get({ url: environment.apiBaseUri + '/heroimages/random/' }).subscribe({
            next: (res) => {
                this.image = res as HeroImage
                this.service.updateState([this.image])
                this.setImage(this.image)
            }
        });

        this.service.get({ url: environment.apiBaseUri + '/heroimages/lustrum/' }).subscribe({
            next: (res) => {
                this.lustrumImage = res as HeroImage
                this.service.updateState([this.lustrumImage])
                if (this.router.url.includes("lustrum")) {
                    this.setImage(this.lustrumImage)
                }
            }
        });

    }

    ngAfterViewInit() {
        this.playBackgroundVideo();
        requestAnimationFrame(() => this.playBackgroundVideo());
    }

    private playBackgroundVideo() {
        const video = this.elRef.nativeElement.querySelector('#video-background') as HTMLVideoElement | null;
        if (!video || this.isLoggedIn$ || !this.isNewsPage) {
            return;
        }

        video.muted = true;
        video.playsInline = true;
        video.loop = true;
        video.autoplay = true;
        video.currentTime = 0;

        const startPlayback = () => {
            video.play().catch(() => {
                setTimeout(() => {
                    const retryVideo = this.elRef.nativeElement.querySelector('#video-background') as HTMLVideoElement | null;
                    if (retryVideo && !this.isLoggedIn$ && this.isNewsPage) {
                        retryVideo.muted = true;
                        retryVideo.currentTime = 0;
                        retryVideo.play().catch(() => startPlayback());
                    }
                }, 250);
            });
        };

        startPlayback();
    }

    setImage(image: HeroImage) {
        this.elRef.nativeElement.style.setProperty('background-image', `url(${environment.mediaBaseURI + image.photo_url})`)
    }
}
